import React, { useState } from 'react'
import { useOrganization } from '../contexts/OrganizationContext';
import { Alert, Button, Card, Col, Container, Form, Row } from 'react-bootstrap';
import { doc, setDoc } from 'firebase/firestore';
import { useFirestore } from 'reactfire';
import { COLLECTIONS, ORGANIZATION_ID } from '../utils/shared/constants';
import { toast } from 'react-toastify';
import { AdvancedButton } from '../components/AdvancedButton';

export default function AdminPage() {
    const organization = useOrganization();
    const firestore = useFirestore();
    const [processing, setProcessing] = useState(false);

    // State for form fields
    const [newGoodsOwner, setNewGoodsOwner] = useState<{ id: string, name: string, username: string, password: string }>({
        id: '',
        name: '',
        username: '',
        password: ''
    });
    const [editMode, setEditMode] = useState<{ active: boolean, index: number }>({ active: false, index: -1 });

    const goodsOwners = organization.goodsOwners || [];

    // Handler for creating/updating goods owner
    const handleSaveGoodsOwner = async () => {
        if (!newGoodsOwner.id || !newGoodsOwner.name || !newGoodsOwner.username || !newGoodsOwner.password) {
            toast.error('Please fill in all fields');
            return;
        }

        try {
            setProcessing(true);

            const newId = parseInt(newGoodsOwner.id);
            if (isNaN(newId)) {
                toast.error('ID must be a number');
                setProcessing(false);
                return;
            }

            // Create a copy of the current goods owners
            const updatedGoodsOwners = [...goodsOwners];

            // If in edit mode, update the existing owner
            if (editMode.active) {
                updatedGoodsOwners[editMode.index] = {
                    id: newId,
                    name: newGoodsOwner.name,
                    username: newGoodsOwner.username,
                    password: newGoodsOwner.password
                };
            } else {
                // Otherwise add a new owner
                updatedGoodsOwners.push({
                    id: newId,
                    name: newGoodsOwner.name,
                    username: newGoodsOwner.username,
                    password: newGoodsOwner.password
                });
            }

            // Update organization in Firestore
            const orgRef = doc(firestore, `${COLLECTIONS.ORGANIZATIONS}/${ORGANIZATION_ID}`);
            await setDoc(orgRef, { goodsOwners: updatedGoodsOwners }, { merge: true });

            // Reset form
            setNewGoodsOwner({ id: '', name: '', username: '', password: '' });
            setEditMode({ active: false, index: -1 });

            toast.success(editMode.active ? 'Goods Owner updated successfully' : 'Goods Owner added successfully');
        } catch (error) {
            console.error('Error saving goods owner:', error);
            toast.error('Failed to save goods owner');
        } finally {
            setProcessing(false);
        }
    };

    // Handler for editing goods owner
    const handleEditGoodsOwner = (index: number) => {
        const ownerToEdit = goodsOwners[index];
        setNewGoodsOwner({
            id: ownerToEdit.id.toString(),
            name: ownerToEdit.name,
            username: ownerToEdit.username || '',
            password: ownerToEdit.password || ''
        });
        setEditMode({ active: true, index });
    };

    // Handler for deleting goods owner
    const handleDeleteGoodsOwner = async (index: number) => {
        try {
            setProcessing(true);

            // Create a copy of the goods owners without the one to delete
            const updatedGoodsOwners = goodsOwners.filter((_, i) => i !== index);

            // Update organization in Firestore
            const orgRef = doc(firestore, `${COLLECTIONS.ORGANIZATIONS}/${ORGANIZATION_ID}`);
            await setDoc(orgRef, { goodsOwners: updatedGoodsOwners }, { merge: true });

            toast.success('Goods Owner deleted successfully');
        } catch (error) {
            console.error('Error deleting goods owner:', error);
            toast.error('Failed to delete goods owner');
        } finally {
            setProcessing(false);
        }
    };

    const handleCancelEdit = () => {
        setNewGoodsOwner({ id: '', name: '', username: '', password: '' });
        setEditMode({ active: false, index: -1 });
    };

    return (
        <Container className="py-4">
            <h1 className="mb-4">Admin - Goods Owners</h1>

            {/* Add/Edit Form */}
            <Card className="mb-4">
                <Card.Header>
                    <h5>{editMode.active ? 'Edit Goods Owner' : 'Add New Goods Owner'}</h5>
                </Card.Header>
                <Card.Body>
                    <Form>
                        <Row className="mb-3">
                            <Col sm={4}>
                                <Form.Group>
                                    <Form.Label>ID</Form.Label>
                                    <Form.Control
                                        type="number"
                                        value={newGoodsOwner.id}
                                        onChange={(e) => setNewGoodsOwner({ ...newGoodsOwner, id: e.target.value })}
                                        placeholder="Enter ID"
                                    />
                                </Form.Group>
                            </Col>
                            <Col sm={8}>
                                <Form.Group>
                                    <Form.Label>Name</Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={newGoodsOwner.name}
                                        onChange={(e) => setNewGoodsOwner({ ...newGoodsOwner, name: e.target.value })}
                                        placeholder="Enter name"
                                    />
                                </Form.Group>
                            </Col>
                        </Row>

                        <Row className="mb-3">
                            <Col sm={6}>
                                <Form.Group>
                                    <Form.Label>Username</Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={newGoodsOwner.username}
                                        onChange={(e) => setNewGoodsOwner({ ...newGoodsOwner, username: e.target.value })}
                                        placeholder="Enter username"
                                    />
                                </Form.Group>
                            </Col>
                            <Col sm={6}>
                                <Form.Group>
                                    <Form.Label>Password</Form.Label>
                                    <Form.Control
                                        type="password"
                                        value={newGoodsOwner.password}
                                        onChange={(e) => setNewGoodsOwner({ ...newGoodsOwner, password: e.target.value })}
                                        placeholder="Enter password"
                                    />
                                </Form.Group>
                            </Col>
                        </Row>

                        <div className="d-flex gap-2">
                            <AdvancedButton
                                variant="primary"
                                processing={processing}
                                onClick={handleSaveGoodsOwner}
                                disabled={!newGoodsOwner.id || !newGoodsOwner.name || !newGoodsOwner.username || !newGoodsOwner.password}
                            >
                                {editMode.active ? 'Update' : 'Add'}
                            </AdvancedButton>

                            {editMode.active && (
                                <Button variant="secondary" onClick={handleCancelEdit}>
                                    Cancel
                                </Button>
                            )}
                        </div>
                    </Form>
                </Card.Body>
            </Card>

            {/* List of Goods Owners */}
            <h2 className="mb-3">Current Goods Owners</h2>

            {goodsOwners.length === 0 ? (
                <Alert variant="info">No goods owners found. Add one using the form above.</Alert>
            ) : (
                <Row xs={1} md={2} lg={3} className="g-4">
                    {goodsOwners.map((owner, index) => (
                        <Col key={index}>
                            <Card>
                                <Card.Body>
                                    <Card.Title>{owner.name}</Card.Title>
                                    <Card.Subtitle className="mb-2 text-muted">ID: {owner.id}</Card.Subtitle>

                                    <p className="mb-1">
                                        <strong>Username:</strong> {owner.username}
                                    </p>
                                    <p className="mb-2">
                                        <strong>Password:</strong>
                                        <span className="ms-1">{owner.password}</span>
                                    </p>
                                    <div className="d-flex gap-2 mt-3">
                                        <Button
                                            variant="outline-primary"
                                            size="sm"
                                            onClick={() => handleEditGoodsOwner(index)}
                                            disabled={processing}
                                        >
                                            <i className="bi bi-pencil me-1"></i> Edit
                                        </Button>
                                        <Button
                                            variant="outline-danger"
                                            size="sm"
                                            onClick={() => handleDeleteGoodsOwner(index)}
                                            disabled={processing}
                                        >
                                            <i className="bi bi-trash me-1"></i> Delete
                                        </Button>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                    ))}
                </Row>
            )}
        </Container>
    )
}
